import type { Ceviri } from "../tip";
import type { donemsel as tr } from "../tr/donemsel";

const gun = (n: number) => (n === 1 ? "day" : "days");
const varlik = (n: number) => (n === 1 ? "asset" : "assets");

/** English dictionary · donemsel. */
export const donemsel: Ceviri<typeof tr> = {
  baslik: "Period Returns",
  aciklama: "For the selected assets and dates: each day, the % return over the period ending that day.",
  tercihlerOkunuyor: "Reading preferences…",
  sunucuyaBagliDegil: "Not connected to the server.",

  kilit: {
    ucretsiz: (n) => `More than ${n} ${varlik(n)}, benchmark series and the basket`,
    kendi: "Assets other than your own, benchmark series and the basket",
    kisildi: "Your saved selection was not deleted; it is shortened to the limit for now.",
    sepet: "Basket",
  },

  periyot: {
    g7: "1 week (7 days)",
    g30: "1 month (30 days)",
    g92: "3 months (92 days)",
    g182: "6 months (182 days)",
    g365: "1 year (365 days)",
    g730: "2 years (730 days)",
    g1095: "3 years (1095 days)",
    g1460: "4 years (1460 days)",
    g1825: "5 years (1825 days)",
  },

  dengeleme: {
    yok: "None — buy and hold",
    aylik: "Monthly",
    ceyreklik: "Quarterly",
    yillik: "Yearly",
  },

  liste: {
    baslik: "Assets",
    seciliSayisi: (n, enCok) => `· ${n} / ${enCok} selected`,
    gizle: "Hide ▲",
    goster: "Show ▼",
    okunamiyor: "The list cannot be read",
    yukleniyor: "Reading the server's asset list…",
    okunamadi: "The asset list could not be read",
    ara: "Search",
    araIpucu: "Symbol or name",
    secimiTemizle: "Clear selection",
    dolu: (n) => `At most ${n} ${varlik(n)} can be selected; deselect one to add another.`,
    bos: "The server is not tracking any assets yet.",
    uyanYok: "No assets match the search.",
    kiyasGrubu: "Benchmark series",
    hazirlaniyorBalon: (ad) => `${ad} — being prepared`,
    hazirlaniyor: " being prepared",
  },

  doldur: {
    baslik: "Fill from accounts",
    altBaslik: "· assets and basket weights from today's values",
    sanal: "virtual",
    hesaplaniyor: "Calculating…",
    secVeOranla: "Select and weight",
    hata: "Could not fill from accounts",
    varlikYok: "The selected accounts hold no assets that Period Returns can chart.",
    secildiSepetli: (n) => `${n} ${varlik(n)} selected, basket weights filled in.`,
    secildiTek: (n) => `${n} ${varlik(n)} selected (a basket needs at least two).`,
    disarida: "Left out:",
  },

  ayarlar: {
    bolge: "Period and options",
    paraBirimi: "Currency",
    periyot: "Return period",
    ozelPeriyot: "Custom (number of days)",
    gun: "Days",
    tamSayiAraligi: (en, enCok) => `A whole number from ${en} to ${enCok}`,
    temettu: "Reinvest dividends",
    sepetCiz: "Also chart the basket return",
    yalnizSepet: "Show the basket only",
    ortalamaEtiket: "Average price: last",
    ortalamaGirdi: "Average price, number of days",
    islemGunu: "trading days",
  },

  sepet: {
    enAzIki: "For a basket, select at least two assets other than benchmark series.",
    dengeleme: "Rebalancing",
    esitDagit: "Split equally",
    kiyasGirmez: "Benchmark series do not enter the basket; they stay on the chart for comparison.",
    gecersiz: "One of the weights is invalid: enter a percentage from 0 to 100 with at most two decimals.",
    toplamTam: "Total 100%.",
    toplamEksik: (yuzde) => `Total ${yuzde}%; the basket is charted only when the total is 100%.`,
    olcekle: "Scale to 100",
  },

  istek: {
    varlikSec: "Select at least one asset from the list above.",
    ikiTarih: "Choose both dates.",
    sira: "The start date cannot be after the end date.",
    enCokGun: (n) => `The range can be at most ${n} ${gun(n)}.`,
    paraSec: "Choose a currency.",
    sepetToplam: "The basket weights must add up to 100% (above).",
  },

  analiz: {
    uyariFiyat: (mesaj) => `price: ${mesaj}`,
    uyariKur: (mesaj) => `exchange rate: ${mesaj}`,
    derlemedeYok: "This build has no Period Returns analysis.",
    sepet: "Basket",
    secimBekleniyor: "Waiting for a selection to chart",
    hesaplanamadi: "The analysis could not be calculated",
    esitleniyor: "Fetching prices and exchange rates from the server…",
    hesaplaniyor: "Calculating…",
    guncelleniyor: "Updating…",
    kismiBaslik: "Part of the data could not be fetched from the server",
    kismiMetin: (ayrinti) => `The chart was drawn with the data already on this device. Details: ${ayrinti}`,
  },

  sonuc: {
    sepet: "Basket",
    gunYokBaslik: "No days to chart",
    gunYokAciklama: "Data for the basket's newest asset starts after the end of the selected range.",
    baslik: (periyot, para) => `${periyot}-day return (${para}`,
    baslikOrtalama: (n) => `, average price: last ${n} trading ${gun(n)}`,
    baslikTemettu: ", dividends included",
    baslikSon: ")",
    gecBaslik: "The chart starts late",
    gecBaslikMetin: (tarih) =>
      `Data for the basket's newest asset starts on ${tarih}; the chart is drawn from that day on.`,
  },

  sicrama: {
    baslik: "Unusual price jump",
    kapanis: "close",
    birIslemGunu: ", in one trading day",
    olayDuzeltilmemis: "The source has a split on this date, but the price does not look adjusted for it.",
    olayYok: "The source has no split or bonus shares record on this date.",
    olayBilinmiyor: "No corporate action information for this instrument.",
    yaniltici: "Returns for periods covering this day may be misleading.",
    isaretBalon: (ad, tarih) => `${ad} ${tarih}: unusual price jump`,
  },

  uc: {
    ortKapanis: (fiyat) => `avg. close ${fiyat}`,
    ortGunler: (n, from, to) => `${n} trading ${gun(n)} ${from}–${to}`,
    kapanis: (fiyat) => `close ${fiyat}`,
    fiyatGunu: (tarih) => `price date ${tarih}`,
    birim: (birim) => `units ${birim} (with dividends)`,
  },

  seciliGun: {
    bolge: "Selected day",
    periyotSonu: (tarih) => `Period end ${tarih}`,
    periyotBasiAciklama: (tarih, n) => `· period start ${tarih} (${n} ${gun(n)} earlier)`,
    varlik: "Asset",
    sutunBasi: "Period start",
    sutunSonu: "Period end",
    getiri: "Return",
    sepetEndeks: "The basket's value is an index: 100 on the day it was set up.",
  },

  motorSoylemedi: "The engine did not say why this value is missing — this is a defect, please report it.",

  ozet: {
    bolge: "Summary",
    baslik: (periyot) => `Summary — spread of ${periyot}-day returns within the period`,
    varlik: "Asset",
    son: "Last",
    enDusuk: "Lowest",
    enYuksek: "Highest",
    ortalama: "Average",
    olculenGun: "Days measured",
  },

  bilgiKutusu: {
    periyotBasi: (tarih, para) => `period start ${tarih} · ${para}`,
  },
};
